/**
 * HVP Cascade Controller - dropdown DOM writes
 *
 * Option patching (via HVPDomPatcher with preserveValue:true), single-option
 * auto-select, downstream clearing, and attribute-name/index helpers.
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CascadeDropdownMethods = {
    /**
     * Update dropdown options
     *
     * Uses smart DOM patching to prevent flickering:
     * - Only adds/removes changed options (no full rebuild)
     * - Preserves current selection if still valid
     * - Maintains correct option order
     *
     * @param {string} attribute - Attribute name
     * @param {Array} options - Array of option objects
     * @param {boolean} animate - Whether to animate the update
     */
    updateDropdownOptions(attribute, options, animate = true) {
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] POPULATING DROPDOWN:', attribute);
        const dropdown = this.getDropdown(attribute);

        if (!dropdown) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   ERROR: Dropdown element not found for', attribute);
            this.log('Dropdown not found:', attribute);
            return;
        }

        if (!Array.isArray(options) || options.length === 0) {
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   WARNING: No options to populate for', attribute);
            this.log('No options for:', attribute);
            return;
        }

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   Options count:', options.length);
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   Option values:', options.map(o => o.value || o.slug || o).slice(0, 5).join(', '), options.length > 5 ? '...' : '');

        const currentValue = dropdown.value;
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   Current value:', currentValue || '(none)');

        // Animate out
        if (animate && this.animationsEnabled) {
            dropdown.classList.add('hvp-updating');
        }

        // Ensure placeholder exists
        const hasPlaceholder = dropdown.options[0]?.value === '';
        if (!hasPlaceholder) {
            const placeholder = document.createElement('option');
            placeholder.value = '';
            placeholder.textContent = 'Choose an option';
            dropdown.insertBefore(placeholder, dropdown.firstChild);
        }

        // Use smart DOM patching instead of full rebuild
        // This prevents flickering and maintains option order
        const changed = this.domPatcher.patchDropdownOptions(dropdown, options, {
            allowRemoval: true,
            preserveValue: true,
            attrName: attribute
        });

        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   DOM patch result: changed =', changed);

        // Update state
        dropdown.disabled = false;
        dropdown.classList.remove('hvp-empty');
        dropdown.classList.toggle('hvp-single-option', options.length === 1);

        // Animate in
        if (animate && this.animationsEnabled) {
            requestAnimationFrame(() => {
                dropdown.classList.remove('hvp-updating');
                if (changed) {
                    dropdown.classList.add('hvp-updated');
                    setTimeout(() => dropdown.classList.remove('hvp-updated'), 300);
                }
            });
        }

        this.log('Updated dropdown:', attribute, `(${options.length} options, changed: ${changed})`);
    },

    /**
     * Auto-select dropdowns with single option
     */
    async autoSelectSingleOptions(startIndex = 0) {
        for (let i = startIndex; i < this.attributeOrder.length; i++) {
            const attr = this.attributeOrder[i];
            const normalized = this.normalizeAttributeName(attr);
            const dropdown = this.getDropdown(normalized);

            if (!dropdown) continue;

            const options = Array.from(dropdown.options).filter(opt => opt.value !== '');

            if (options.length === 1) {
                const value = options[0].value;

                if (dropdown.value !== value) {
                    dropdown.value = value;
                    dropdown.classList.add('hvp-auto-selected');
                    this.selections[normalized] = value;

                    this.log('Auto-selected:', normalized, '=', value);

                    // Load next options
                    if (i < this.attributeOrder.length - 1) {
                        await this.loadBatchOptions();
                    }
                }
            } else if (this.selections[normalized] || dropdown.value) {
                // Multi-option attribute that is ALREADY chosen (WooCommerce
                // default_attributes, URL preselect, restored form). Adopt the
                // DOM value into state if missing and keep walking so the
                // single-option attributes BELOW it still get auto-selected —
                // otherwise areAllParentsSelected() skips every downstream
                // dropdown and the page loads un-narrowed (partial defaults
                // dead-end: an invalid size stays pickable, then every later
                // select goes empty).
                if (!this.selections[normalized]) {
                    this.selections[normalized] = dropdown.value;
                    this.log('Adopted pre-selected value:', normalized, '=', dropdown.value);
                }
            } else {
                // Stop at first unselected multi-option
                break;
            }
        }

        // Check if complete
        if (this.areAllSelectionsComplete()) {
            await this.findMatchingVariation();
        }
    },

    /**
     * Clear subsequent dropdowns
     */
    clearSubsequentDropdowns(fromIndex) {
        for (let i = fromIndex + 1; i < this.attributeOrder.length; i++) {
            const attr = this.attributeOrder[i];
            const normalized = this.normalizeAttributeName(attr);
            const dropdown = this.getDropdown(normalized);

            if (dropdown) {
                const placeholder = dropdown.options[0]?.textContent || 'Choose an option';

                dropdown.innerHTML = '';

                const placeholderOption = document.createElement('option');
                placeholderOption.value = '';
                placeholderOption.textContent = placeholder;
                dropdown.appendChild(placeholderOption);

                dropdown.value = '';
                dropdown.disabled = true;
                dropdown.classList.add('hvp-empty');
                dropdown.classList.remove('hvp-single-option', 'hvp-auto-selected');
            }

            // Clear from selections
            delete this.selections[normalized];
        }
    },

    /**
     * Select attribute value
     */
    selectAttribute(attribute, value) {
        const normalized = this.normalizeAttributeName(attribute);
        const dropdown = this.getDropdown(normalized);

        if (dropdown) {
            dropdown.value = value;
            dropdown.dispatchEvent(new Event('change', { bubbles: true }));
        }
    },

    /**
     * Clear attribute selection
     */
    clearSelection(attribute) {
        this.selectAttribute(attribute, '');
    },

    /**
     * Get dropdown element
     */
    getDropdown(attribute) {
        const normalized = this.normalizeAttributeName(attribute);
        return this.dropdowns.get(normalized) || null;
    },

    /**
     * Get attribute index
     */
    getAttributeIndex(attribute) {
        const normalized = this.normalizeAttributeName(attribute);

        return this.attributeOrder.findIndex(attr => {
            return this.normalizeAttributeName(attr) === normalized;
        });
    },

    /**
     * Normalize attribute name
     */
    normalizeAttributeName(name) {
        // Remove 'attribute_' prefix if present
        let normalized = name.replace('attribute_', '');

        // Ensure 'pa_' prefix for product attributes
        if (!normalized.startsWith('pa_')) {
            normalized = 'pa_' + normalized.replace('pa_', '');
        }

        return normalized;
    },
};

export default CascadeDropdownMethods;
